import { ReactNode, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';
import makeUrls from '@culturehq/add-to-calendar/dist/makeUrls';

import { Link, useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import Modal, {
	ModalBackground,
	ModalContent,
} from 'packages/kakapo-ui-kit/components/modals/Modal';
import ModalHeader, {
	ModalHeaderTitle,
} from 'packages/kakapo-ui-kit/components/modals/ModalHeader';
import CalendarIcon from 'packages/kakapo-ui-application/pages/learning/components/CalendarIcon';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import LearningEventHeader from 'packages/kakapo-ui-application/pages/learning/components/LearningEventHeader';
import LearningEventAttendeeAvatars from 'packages/kakapo-ui-application/pages/learning/components/LearningEventAttendeeAvatars';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';
import AwardIcon from 'packages/kakapo-ui-kit/assets/icons/AwardIcon';

import LearningEventAttendee from '../components/LearningEventAttendee';
import { ActionArchiveListItem } from 'packages/kakapo-ui-kit/components/actions/ActionListItem';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import { Button } from '@treams/treams-ui';
import {
	AppleLogoIcon,
	GoogleLogoIcon,
	MicrosoftOutlookIcon,
	CopyIcon,
} from '@treams/treams-icons';

const Anchor = styled(Link)`
	color: ${(props) => props.theme.colors.primary};

	&:hover {
		text-decoration: underline;

		&:active {
			color: black;
		}
	}
`;

const noWrapStyle = `
	display: block;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	width: 100%;
`;

const PlainAnchor = styled.a<{ noWrap?: boolean }>`
	color: ${(props) => props.theme.colors.primary};

	${(props) => (props.noWrap ? noWrapStyle : '')}

	&:hover {
		text-decoration: underline;

		&:active {
			color: black;
		}
	}
`;

const LearningEventDetailLabel = styled(Box)`
	font-size: 14px;
	color: ${(props) => props.theme.colors.slateBlue};
`;

const Alert = styled(Box)`
	background-color: ${(props) => props.theme.colors.red};
	border-radius: 7px;
	color: #ffffff;
`;

function LearningEventDetail(props: { label: string; value: ReactNode }) {
	return (
		<Flex py={1}>
			<LearningEventDetailLabel flexGrow={1}>{props.label}</LearningEventDetailLabel>
			<Box width={300} flexShrink={0} flexGrow={0} textAlign="right">
				{props.value}
			</Box>
		</Flex>
	);
}

function LearningEventModal(props: { event: graphql.LearningEvent; returnURL?: string }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const history = useHistory();
	const roles = useViewerRoles();
	const enabledLearningCertificates = useFeature(graphql.FeatureName.LearningCertificates);

	const { data: certificateTemplatesData } = graphql.useLearningCertificateTemplatesQuery({
		variables: { isArchived: false },
	});

	const organizers = props.event.hosts.edges.filter(
		({ attendeeType }) =>
			attendeeType === graphql.LearningEventAttendeeType.Organizer ||
			attendeeType === graphql.LearningEventAttendeeType.HostOrganizer,
	);

	const hosts = props.event.hosts.edges.filter(
		({ attendeeType }) =>
			attendeeType === graphql.LearningEventAttendeeType.Host ||
			attendeeType === graphql.LearningEventAttendeeType.HostOrganizer,
	);

	const event = {
		name: localize(props.event.title).text,
		details: [
			localize(props.event.description ?? [])
				?.text.ops.map((op) => op.insert ?? '')
				.join('') ?? undefined,
			props.event.meetingLink ?? undefined,
		]
			.filter(Boolean)
			.join(' - '),
		location: props.event.location ?? '',
		startsAt: props.event.startDate.toString(),
		endsAt: props.event.endDate.toString(),
	};

	const links = makeUrls(event);

	const cancelled = props.event.status === graphql.LearningEventStatus.Cancelled;

	const canAwardCertificates =
		(roles.isWorkspaceSuperuser || roles.isLearningOfficer) &&
		certificateTemplatesData?.learningCertificateTemplates.edges.length !== 0;

	const isSuperUser = roles.isWorkspaceSuperuser;
	const isLearningOfficer = roles.isLearningOfficer;
	const canDuplicate = isSuperUser || isLearningOfficer;

	const handleOnCopyClick = () => {
		history.push({
			pathname: `/learning/events/copy?eventID=${props.event.id}`,
			state: {
				backgroundLocation: history.location,
			},
		});
	};

	const calendarButtons = [
		[
			{
				title: 'Apple Calendar',
				link: 'ics',
				icon: (
					<SmallerIcon>
						<AppleLogoIcon />
					</SmallerIcon>
				),
			},
			{
				title: 'Google Calendar',
				link: 'google',
				icon: (
					<SmallerIcon>
						<GoogleLogoIcon />
					</SmallerIcon>
				),
			},
		],
		[
			{
				title: 'Outlook Web',
				link: 'outlook',
				icon: <MicrosoftOutlookIcon />,
			},
			{
				title: 'Outlook Desktop',
				link: 'ics',
				icon: <MicrosoftOutlookIcon />,
			},
		],
	];

	return (
		<ModalBackground>
			<Modal>
				<ModalHeader
					onCancel={() => {
						if (!!props.returnURL) {
							return history.push(props.returnURL);
						}

						if (!!history.location.state?.backgroundLocation) {
							return history.push(history.location.state.backgroundLocation);
						}

						history.push('/learning');
					}}
					leftIconButton={
						canDuplicate && (
							<Box display="flex">
								<LearningEventAttendeeAvatars event={props.event} />
								<Button
									type="primary"
									icon={<CopyIcon />}
									mr={2}
									onClick={() => handleOnCopyClick()}
								/>
							</Box>
						)
					}
					rightIconButton={<CalendarIcon date={props.event.startDate} />}
				>
					<Flex alignItems="center" justifyContent="space-between">
						<Flex flexGrow={1} flexBasis={1} justifyContent="center">
							<ModalHeaderTitle>{t('learning.event.modal.title')}</ModalHeaderTitle>
						</Flex>
					</Flex>
				</ModalHeader>

				<LearningEventHeader event={props.event} />

				<ModalContent>
					{cancelled && (
						<Alert py={2} px={3} mb={3}>
							{t('learning.event.modal.cancelled')}
						</Alert>
					)}

					{enabledLearningCertificates && canAwardCertificates && (
						<>
							<Box mb={2}>
								<SectionTitle>{t('learning.event.modal.actions.title')}</SectionTitle>
							</Box>

							<Box mb={3}>
								<HorizontalRule />
							</Box>

							<Flex mb={4}>
								<Box mr={2}>
									<Button
										type="transparent"
										link={`/learning/event/${props.event.id}/award-certificates`}
										navigationState={{
											backgroundLocation: history.location.state?.backgroundLocation,
										}}
										title={t('learning.event.modal.actions.awardCertificates')}
										icon={<AwardIcon />}
									/>
								</Box>

								<Button
									type="transparent"
									link={`/learning/event/${props.event.id}/attendees`}
									navigationState={{
										backgroundLocation: history.location.state?.backgroundLocation,
									}}
									title={t('learning.event.modal.attendees.viewAll')}
								/>
							</Flex>
						</>
					)}

					{!cancelled && (
						<Box mb={4}>
							<Box my={2}>
								<SectionTitle>{t('learning.event.modal.addToCalendar.button.title')}</SectionTitle>
							</Box>

							<Box mb={3}>
								<HorizontalRule />
							</Box>

							{calendarButtons.map((group) => (
								<StyledLearningEventCalendarButtons mt={2}>
									{group.map(({ title, link, icon }, idx) => (
										<Button
											key={`title-${idx}`}
											small
											title={title}
											icon={icon}
											onClick={() => {
												window.open(links[link]);
											}}
											target="_blank"
										/>
									))}
								</StyledLearningEventCalendarButtons>
							))}
						</Box>
					)}

					<Box mb={2}>
						<SectionTitle>{t('learning.event.modal.introduction.title')}</SectionTitle>
					</Box>

					<Box mb={3}>
						<HorizontalRule />
					</Box>

					<Box mb={4}>
						<QuillText quill={localize(props.event.description ?? [])?.text} />
					</Box>

					<Box mb={2}>
						<SectionTitle>{t('learning.event.modal.details.title')}</SectionTitle>
					</Box>

					<HorizontalRule />

					<Box mb={4} mt={2}>
						<LearningEventDetail
							label={t('learning.event.modal.details.startTime.label')}
							value={moment(props.event.startDate).format('HH:mm')}
						/>
						<LearningEventDetail
							label={t('learning.event.modal.details.endTime.label')}
							value={moment(props.event.endDate).format('HH:mm')}
						/>
						<LearningEventDetail
							label={t('learning.event.modal.meta.language.label')}
							value={t(`common.languages.${props.event.languageID}`)}
						/>
						{props.event.location && (
							<LearningEventDetail
								label={t('learning.event.modal.details.location.label')}
								value={props.event.location}
							/>
						)}
						{props.event.meetingLink && (
							<LearningEventDetail
								label={t('learning.event.modal.details.meetingLink.label')}
								value={
									<PlainAnchor href={props.event.meetingLink} target="_blank" noWrap>
										{props.event.meetingLink}
									</PlainAnchor>
								}
							/>
						)}
						<LearningEventDetail
							label={t('learning.event.modal.details.maxAttendees.label')}
							value={props.event.capacity.toFixed()}
						/>
					</Box>

					{organizers.length > 0 && (
						<>
							<Box my={2}>
								<SectionTitle>{t('learning.event.modal.organizers.title')}</SectionTitle>
							</Box>

							<HorizontalRule />

							<Box mt={2} mb={4}>
								{organizers.map((host) => (
									<LearningEventAttendee
										key={host.node.id}
										user={host.node}
										type={host.attendeeType}
										readonly={true}
									/>
								))}
							</Box>
						</>
					)}

					{hosts.length > 0 && (
						<>
							<Box my={2}>
								<SectionTitle>{t('learning.event.modal.hosts.title')}</SectionTitle>
							</Box>

							<HorizontalRule />

							<Box mb={4}>
								{hosts.map((host) => (
									<LearningEventAttendee
										key={host.node.id}
										user={host.node}
										type={host.attendeeType}
										readonly={true}
									/>
								))}
							</Box>
						</>
					)}

					<Box mb={4}>
						<Anchor
							to={`/learning/event/${props.event.id}/attendees`}
							navigationState={{
								backgroundLocation: history.location.state?.backgroundLocation,
							}}
						>
							{t('learning.event.modal.attendees.viewAll')} »
						</Anchor>
					</Box>

					{cancelled && (
						<>
							<Box my={2}>
								<SectionTitle>{t('learning.event.modal.actions.title')}</SectionTitle>
							</Box>

							<Box mb={3}>
								<HorizontalRule />
							</Box>

							<Box mt={4}>
								<ActionArchiveListItem
									archivableItem={props.event}
									archivableItemName="event"
									archivableItemKey="event"
									useArchive={graphql.useArchiveLearningEventMutation as any}
									useRestore={graphql.useRestoreLearningEventMutation as any}
								/>
							</Box>
						</>
					)}
				</ModalContent>
			</Modal>
		</ModalBackground>
	);
}

export default function LearningEventModalID(props: { eventID: string; returnURL?: string }) {
	const { data } = graphql.useLearningEventQuery({
		variables: {
			id: props.eventID,
		},
	});

	if (data) {
		return <LearningEventModal event={data.learningEvent as graphql.LearningEvent} />;
	}

	return (
		<ModalBackground>
			<Modal>
				<Box py={5}>
					<Spinner isLoading size="18px" />
				</Box>
			</Modal>
		</ModalBackground>
	);
}

const StyledLearningEventCalendarButtons = styled(Box)`
	display: flex;
	gap: 5px;

	& > * {
		width: 100%;
	}
`;

const SmallerIcon = styled(Box)`
	svg {
		scale: 80%;
	}
`;
